// Global
import React, { useMemo } from 'react';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';

// Local
import { Content } from '@generated/Content/AlertBanner.model';
import { AlertBanner } from './AlertBanner';
import {
  usePlainTextWrapper,
  PlainTextWrapperProps,
} from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useImageWrapper } from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { Themes } from 'lib/themes';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';

type AlertBannerProps = Content.AlertBanner.AlertBanner_Component;

// check if a date is empty/default from Sitecore
const isEmptyDate = (dateValue?: string) => {
  return !dateValue || dateValue === '0001-01-01T00:00:00Z' || dateValue === '';
};

interface AlertBannerItemProps {
  fields: Content.AlertBanner.AlertBanner['fields'];
  itemId: string;
  isDismissed: boolean;
  theme?: Themes;
}

const AlertBannerItem = ({ fields, itemId, isDismissed, theme }: AlertBannerItemProps) => {
  // Use hooks to render fields
  const title = usePlainTextWrapper({ field: fields?.title } as PlainTextWrapperProps);
  const text = useRichTextWrapper({ field: fields?.text });
  const icon = useImageWrapper({ field: fields?.icon });

  if (!fields) return null;

  // Process date values - convert Sitecore empty dates to undefined
  const startDate = isEmptyDate(fields?.startDate?.value?.toString())
    ? undefined
    : fields?.startDate?.value?.toString();

  const endDate = isEmptyDate(fields?.endDate?.value?.toString())
    ? undefined
    : fields?.endDate?.value?.toString();

  return (
    <AlertBanner
      key={itemId}
      theme={theme}
      title={title}
      text={text}
      icon={icon}
      hideIcon={fields?.hideIcon?.value}
      primaryCta={
        <LinkWrapper
          field={fields?.primaryCta}
          ctaComponentClass="component-alert-banner-button-color-1"
          ctaStyle="filled"
          suppressNewTabIcon={true}
          contentResultAnalytics={{
            contentListName: fields?.title?.value?.trim() || undefined,
          }}
        />
      }
      secondaryCta={
        <LinkWrapper
          field={fields?.secondaryCta}
          ctaComponentClass="component-alert-banner-button-color-2"
          ctaStyle="outline"
          suppressNewTabIcon={true}
          contentResultAnalytics={{
            contentListName: fields?.title?.value?.trim() || undefined,
          }}
        />
      }
      startDate={startDate}
      endDate={endDate}
      isDismissed={isDismissed}
      bannerItemId={itemId}
    />
  );
};

AlertBannerItem.displayName = 'AlertBannerItem';

export const Default = (props: AlertBannerProps) => {
  const { sitecoreContext } = useSitecoreContext();

  const siteSettings = sitecoreContext?.siteSettings as SiteSettings & {
    siteAlert?: {
      fields?: {
        siteAlert?: Content.AlertBanner.AlertBanner_Item | Content.AlertBanner.AlertBanner_Item[];
      };
    };
  };

  const dismissedBannerIds = useMemo(
    () => (sitecoreContext?.dismissedBannerIds as string[] | undefined) || [],
    [sitecoreContext?.dismissedBannerIds]
  );

  const theme = props.params?.theme as Themes;

  // If component has its own datasource, render single banner
  // Note: Content SDK sets props.fields to {} (truthy) even when no datasource is configured,
  // unlike JSS 22.x which returned null. Guard against this by also checking dataSource.
  if (props.fields && props.rendering?.dataSource) {
    const bannerItemId = props.rendering?.dataSource || 'default';
    const isDismissed = dismissedBannerIds.includes(bannerItemId);
    return (
      <AlertBannerItem
        fields={props.fields}
        itemId={bannerItemId}
        isDismissed={isDismissed}
        theme={theme}
      />
    );
  }

  // Handle site settings alert banner(s)
  const siteAlertData = siteSettings?.siteAlert?.fields?.['siteAlert'];

  // Early return if no site alert data
  if (!siteAlertData) {
    return null;
  }

  // Handle multilist (array) or single item
  const siteAlertItems = Array.isArray(siteAlertData) ? siteAlertData : [siteAlertData];

  // Filter and render banners in a single pass
  const banners = siteAlertItems
    .filter((item) => item?.fields && item?.id)
    .map((item) => {
      const isDismissed = dismissedBannerIds.includes(item.id || '');
      return (
        <AlertBannerItem
          key={item.id}
          fields={item.fields}
          itemId={item.id || ''}
          isDismissed={isDismissed}
          theme={theme}
        />
      );
    });

  return banners.length > 0 ? <>{banners}</> : null;
};
